import { ChunkType } from "@/app/report/common/export/paginate"

import { TFunction } from "i18next"
import { Dispatch, SetStateAction } from "react"

import {
  SectionSessionDetails,
  SectionSummary,
  SectionGraph,
  SectionStats,
  SectionScatterChart,
  SectionTacticMap,
  SectionPerformanceProfile,
} from "@/app/report/common/constructor"
import { REPORT_TYPE, SECTION_TYPE, VALUE_POSITION_TYPE } from "@/app/report/common/constants"
import {
  ChartSessionPlayerSummary,
  IntervalSessionSummaryType,
  PlayerSessionSummary,
  ReportTemplate,
} from "@/types/report"
import { Unit } from "@/types/setting"
import { MetricMeta } from "@/types/metric"
import { MetricThreshold } from "@/types/threshold"

// DAILY, TREND ---------------------------------------------------------
export type GraphOrientation = "horizontal" | "vertical"
export type GraphSettings = {
  title: string
  calcType: string
  yAxis: GraphSettingsYAxis[]
  dataLabel?: string
  decimalPlace?: number
  sortSetting?: {
    key: string
    order: "asc" | "desc"
  }
  /** 차트 방향 — horizontal(선수가 X축, 기본) | vertical(선수 세로 나열). 없으면 horizontal (#185, 서버 #184) */
  orientation?: GraphOrientation
  /** 세로 차트의 X축(값 눈금) 표시 — 없으면 true (#185) */
  showAxis?: boolean
}
/** Custom Table 요약 행(Total/Average) 표시 여부 — 필드가 없으면(기존 템플릿) 둘 다 미표시 (#155) */
export type SummaryRowsSetting = {
  total: boolean
  average: boolean
}
export type StatsSettings = {
  title: string
  fixedColumns: string[]
  calcType: string
  metrics: string[]
  additionalColumns?: string[]
  sortSetting?: {
    key: string
    order: "asc" | "desc"
  }
  groupKey1?: string
  groupKey2?: string | null
  thresholds?: MetricThreshold[]
  summaryRows?: SummaryRowsSetting
}
export type GraphSettingsYAxis = {
  metric: string
  chartType: string
  color: string
  unit?: string
  manualScaleActive: boolean
  manualScaleValue: number | null
}

// DAILY ---------------------------------------------------------
export type SessionDetailsSettings = {
  calcType: string
  yAxis: GraphSettingsYAxis[]
  dataLabel?: string
  decimalPlace?: number
  sortSetting?: {
    key: string
    order: "asc" | "desc"
  }
}
export type ScatterChartSettings = {
  xAxis: string
  yAxis: string
  color: string
  xAxisManualScaleActive?: boolean
  xAxisManualScaleValue?: number | null
  yAxisManualScaleActive?: boolean
  yAxisManualScaleValue?: number | null
}
export type SummarySettings = {
  title: string
  metric: string
  calcType: string
}
export type TacticMapSettings = {
  title: string
  color: string
  defaultMapType: string
  fixedColumns: string[]
  defaultActionType: string
}
export type PerformanceProfileSettings = {
  title: string
  color: string
  yAxis: PerformanceProfileSettingsYAxis[]
  actions: PerformanceProfileSettingsActions[]
}
export type PerformanceProfileSettingsActions = {
  actionCode: string
  color: string
}
export type PerformanceProfileSettingsYAxis = {
  dataType: string
  color: string
  manualScaleActive: boolean
  manualScaleValue?: number | null
}

// -- Section 관련 정의 ---------------------------------------------------------
export type TypeSection =
  | SectionSummary
  | SectionGraph
  | SectionStats
  | SectionScatterChart
  | SectionSessionDetails
  | SectionTacticMap
  | SectionPerformanceProfile

// 테이블 구조의 데이터 모음 ----------------------------------------------------------
export type TableRow = Array<string | number | null>
export type DataTable = Array<TableRow>

// 옵션 메뉴 데이터 타입 ----------------------------------------------------------------
export type CardOption = {
  name: string
  options?: CardOption[]
  action?: () => void
  selected?: boolean
  color?: string
}

// 섹션 별 여러 옵션 경로들의 타입 ----------------------------------------------------------
export type ActivityOptionPath = {
  [key: SECTION_TYPE]: string[][]
}

export type ActivityOptionPathRecord = Record<SECTION_TYPE, string[][]>

// custom type
export type ReportChartFilter = {
  activitySessionType: string // "activity" | "session"
  selectedActivityIds: number[]
  selectedPlayerIds: number[]
  selectedSessionIds: number[]
  selectedIdfTemplate: number[]
}
export type ReportChartActivity = {
  intervalSummary: Array<Array<IntervalSessionSummaryType>>
  playerActivitySummary: PlayerSessionSummary | null
}
export type ReportChartSession = {
  intervalSummary: Array<Array<IntervalSessionSummaryType>>
  playerSummary: ChartSessionPlayerSummary[] | null
  playerSessionSummary: PlayerSessionSummary | null
  selectedSessionIdsForStats: number[]
}

export type ReportCommonProps = {
  metricList: MetricMeta[]
  selectedUnitList: Unit[]
  players: PlayerSessionSummary
  t: TFunction<"translation", undefined>
}
export type ReportActivitySessionProps = {
  activity?: ReportChartActivity
  session?: ReportChartSession
}
export type ReportSetActivitySessionProps = {
  setActivity: Dispatch<SetStateAction<ReportChartActivity>>
  setSession: Dispatch<SetStateAction<ReportChartSession>>
}

export type MenuActionType = {
  value: string // 선택 및 호버 상태 관리를 위한 값
  name: string // 번역키, 없는 경우 보여질 텍스트 (A - Z 등)
  group?: string // 뎁스 1일 경우의 그룹 번역키
  icon?: string
  color?: string // metric 앞에 나타날 색상
  func?: () => void // 가장 하위 뎁스일 경우의 클릭 이벤트
  children?: MenuActionType[] // 자식 메뉴
}

export type MetricMapType = Map<string, MetricMeta>

// SETTINGS
export type SettingSectionProps<T> = {
  settings: T
  tempTemplate: ReportTemplate | null
  setTempTemplate: Dispatch<SetStateAction<ReportTemplate | null>>
}

// -- Export 관련 정의 -----------------------------------------------------------
export const EXPORT_TYPE = {
  PNG: "png",
  PDF: "pdf",
  CSV: "csv",
}
export type EXPORT_TYPE = (typeof EXPORT_TYPE)[keyof typeof EXPORT_TYPE]

export type ExportFileFunctionType = {
  exportType: EXPORT_TYPE
  magnification: number
  reportType: REPORT_TYPE
  chartAnnotation: VALUE_POSITION_TYPE
  decimalPoints: number
}
export type ExportChartRefType = {
  drawChart: () => void
  exportFile: (args: ExportFileFunctionType) => Promise<void>
}

export type ChartAnnotationOptions =
  | VALUE_POSITION_TYPE.HIDE_VALUES
  | VALUE_POSITION_TYPE.ABOVE
  | VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION

export type ImgData = {
  data: string
  width: number
  height: number
  class?: DOMTokenList
  /** 페이지 배치용 컴포넌트 종류 — 캡처 시점에 요소를 보고 정한다 (common/export/chunks) */
  chunkType?: ChunkType
}

export type PrintBasicSet = {
  contentWidth: number
  margin: number
  startX: number
  startY: number
  footerY: number
  footerFontSize: number
}
